import { tracked } from '@glimmer/tracking';

import type { GeneralComponentClass } from 'webapp-types';
import { sanitize } from 'webapp/utils/html';

import InfolayerTypeGeneralComponent from 'webapp/components/infolayer/type-general';

import type {
	ConstructorData as BaseConstructorData,
	onCloseFn,
} from 'webapp/models-custom/infolayer-item/base';
import InfolayerItemBase, {
	EXPIRE_ERROR,
	EXPIRE_INFO,
	EXPIRE_OK,
	EXPIRE_WARNING,
} from 'webapp/models-custom/infolayer-item/base';

export enum Type {
	OK = 'ok',
	ERROR = 'error',
	LOADING = 'loading',
	WARNING = 'warning',
	INFO = 'info',
}

export interface ActionButton {
	text: string;
	onClick: () => void;
	theme?: string;
}

export interface ConstructorData extends BaseConstructorData {
	text: string;
	type: Type;
	actionButton?: ActionButton;
}

export default class InfolayerItemGeneral extends InfolayerItemBase {
	componentClass: GeneralComponentClass = InfolayerTypeGeneralComponent;

	@tracked text: string;
	actionButton: ActionButton | null = null;

	constructor(data: ConstructorData, onClose: onCloseFn) {
		super(data, onClose);

		this.text = sanitize(data.text);
		this.type = data.type;

		if (data.actionButton) {
			this.actionButton = data.actionButton;
		}
	}

	@tracked declare _type: Type;
	set type(value: Type) {
		this._type = value;
		this.checkType();
	}

	get type(): Type {
		return this._type;
	}

	override get classes(): string {
		let classes = super.classes;
		classes += ` ${this.type}`;
		return classes;
	}

	ok(text: string, expire?: number): InfolayerItemGeneral {
		this.type = Type.OK;
		this.text = sanitize(text);
		this.preventClose = false;

		if (expire === undefined) {
			expire = EXPIRE_OK;
		}

		this.expire(expire);

		return this;
	}

	okWithActionButton(
		text: string,
		actionButton: ActionButton,
		expire?: number,
	): InfolayerItemGeneral {
		this.type = Type.OK;
		this.text = sanitize(text);
		this.actionButton = actionButton;
		this.preventClose = false;

		if (expire === undefined) {
			expire = EXPIRE_OK;
		}

		this.expire(expire);

		return this;
	}

	error(text: string, expire?: number): InfolayerItemGeneral {
		this.type = Type.ERROR;
		this.text = sanitize(text);
		this.preventClose = false;

		if (expire === undefined) {
			expire = EXPIRE_ERROR;
		}

		this.expire(expire);

		return this;
	}

	errorWithActionButton(
		text: string,
		actionButton: ActionButton,
		expire?: number,
	): InfolayerItemGeneral {
		this.type = Type.ERROR;
		this.text = sanitize(text);
		this.actionButton = actionButton;
		this.preventClose = false;

		if (expire === undefined) {
			expire = EXPIRE_ERROR * 2;
		}

		this.expire(expire);

		return this;
	}

	warning(text: string, expire?: number): InfolayerItemGeneral {
		this.type = Type.WARNING;
		this.text = sanitize(text);
		this.preventClose = false;

		if (expire === undefined) {
			expire = EXPIRE_WARNING;
		}

		this.expire(expire);

		return this;
	}

	info(text: string, expire?: number): InfolayerItemGeneral {
		this.type = Type.INFO;
		this.text = sanitize(text);
		this.preventClose = false;

		if (expire === undefined) {
			expire = EXPIRE_INFO;
		}

		this.expire(expire);

		return this;
	}

	loading(text: string, expire?: number): InfolayerItemGeneral {
		this.type = Type.LOADING;
		this.text = sanitize(text);
		this.preventClose = true;

		this.expire(expire);

		return this;
	}

	checkType(): void {
		if (this.type === Type.ERROR) {
			try {
				// add custom variable 'error' to livesession
				if (window.__ls) {
					window.__ls('setCustomParams', { params: { error: this.text } });
				}
			} catch (_e) {
				// skip
			}
		}
	}

	checkIfDuplicated(items: InfolayerItemBase[]): boolean {
		return items.some(
			(item) =>
				item instanceof InfolayerItemGeneral &&
				item.type === Type.ERROR &&
				item.text.trim().toLowerCase() === this.text.trim().toLowerCase(),
		);
	}
}
